import { ArrowRight } from 'lucide-react';
import { Explain } from '@/components/Explain';
import { OutcomeChip } from '@/components/OutcomeChip';
import { CopyId, Money } from '@/components/primitives';
import { cn } from '@/lib/cn';
import { deriveOutcome } from '@/lib/bet';
import { formatDuration, parseMysqlTime } from '@/lib/format';
import type { BetBundle } from '@/types';

/**
 * The three-second answer: what is this bet, what did it pay, how did it end.
 * Everything else on the page is evidence for this strip.
 */
export function VerdictStrip({ bundle }: { bundle: BetBundle }) {
  const trx = bundle.transactions.data ?? [];
  const currency = bundle.currencyMetadata.data;
  const game = bundle.resolved.game;

  // For a multi-stake round (blackjack) the "bet" is the whole round, not the
  // one hand the lookup landed on. Use the round's transactions/totals so the
  // 3-second answer isn't a single hand mistaken for the result.
  const round = bundle.round?.data ?? null;
  const roundTrx = round ? round.wagers.flatMap((w) => w.transactions) : trx;

  const bet = trx.find((t) => t.action === 'bet');
  const wins = (round ? roundTrx : trx).filter((t) => t.action === 'win');
  const rollbacks = (round ? roundTrx : trx).filter((t) => t.action === 'rollback');

  const staked = round
    ? String(round.round?.total_bet_amount ?? roundTrx.filter((t) => t.action === 'bet').reduce((s, t) => s + Number(t.amount ?? 0), 0))
    : (bet?.amount ?? null);
  const won = round
    ? Number(round.round?.total_won_amount ?? wins.reduce((s, w) => s + Number(w.amount ?? 0), 0))
    : wins.reduce((sum, w) => sum + Number(w.amount ?? 0), 0);
  const outcome = deriveOutcome(round ? roundTrx : trx);

  const times = trx.map((t) => parseMysqlTime(t.created_at)).filter(Boolean) as Date[];
  const span =
    times.length > 1
      ? Math.max(...times.map((d) => d.getTime())) - Math.min(...times.map((d) => d.getTime()))
      : null;

  const player = trx.find((t) => t.player_id)?.player_id ?? null;

  return (
    <section className="border-b border-hairline bg-surface">
      <div className="flex flex-wrap items-stretch divide-x divide-hairline">
        {round ? (
          <Cell label={<Explain term="round_id">round</Explain>} wide>
            <div className="flex items-baseline gap-2">
              <CopyId value={round.round_id} className="text-[17px] font-semibold" />
              <span className="font-condensed text-[10px] uppercase tracking-wider text-text-faint">
                {round.wagers.length} {round.wagers.length === 1 ? 'hand' : 'hands'}
              </span>
            </div>
          </Cell>
        ) : (
          <Cell label={<Explain term="bet_id">bet</Explain>} wide>
            <CopyId value={bundle.resolved.bet_id} className="text-[17px] font-semibold" />
          </Cell>
        )}

        <Cell label="game">
          <div className="flex items-baseline gap-1.5">
            <span className="font-sans text-[13px] text-text">{String(game?.name ?? '—')}</span>
            {game?.type ? (
              <span className="font-condensed text-[10px] uppercase tracking-wider text-text-faint">
                {String(game.type)}
              </span>
            ) : null}
          </div>
        </Cell>

        <Cell label={<Explain term="play_service_id">operator</Explain>}>
          <CopyId value={bundle.resolved.play_service_id} />
        </Cell>

        <Cell label="player">
          <CopyId value={player} />
        </Cell>

        <Cell label="stake → payout" wide>
          <div className="flex items-center gap-2">
            <Money amount={staked} meta={currency} currency={bet?.currency} tone="bet" />
            <ArrowRight size={11} className="text-text-faint" />
            {wins.length ? (
              <Money amount={won} meta={currency} currency={wins[0]?.currency} tone="win" />
            ) : (
              <span className="font-mono text-text-faint">—</span>
            )}
          </div>
        </Cell>

        <Cell label="outcome">
          <OutcomeChip outcome={outcome} />
        </Cell>

        {span !== null && (
          <Cell label="duration">
            <span className="tnum font-mono text-text-dim">{formatDuration(span)}</span>
          </Cell>
        )}

        <Cell label={<Explain term="cycle_uniq_id">cycle</Explain>}>
          <CopyId value={bundle.resolved.cycle_uniq_id} truncate className="text-text-dim" />
        </Cell>

        {rollbacks.length > 0 && (
          <Cell label="rollbacks">
            <span className="tnum font-mono text-warn">{rollbacks.length}</span>
          </Cell>
        )}
      </div>
    </section>
  );
}

function Cell({
  label,
  children,
  wide,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={cn('px-4 py-2', wide && 'min-w-[180px]')}>
      <div className="eyebrow mb-0.5">{label}</div>
      <div className="flex h-[22px] items-center">{children}</div>
    </div>
  );
}
